import Transform from '@ember-data/serializer/transform'
import type { Type } from '@warp-drive/core-types/symbols'

/* This is necessary so that ember-data knows how to compare two objects for this attribute. Otherwise, if the attribute
 * appears in the payload, it'll always trigger an update, even if they're exactly the same. This can cause unexpected
 * re-renders or other page updates.
 *
 * You should not make changes to an attribute using this transform - it is readonly and does not track any key/value
 * changes. If you need that level of control, you should be creating a new Model & relationship.
 *
 * This is extending upon the approach suggested by ember-data docs for readonly nested data, but with a transform that
 * will stop unexpected updates to an attribute.
 * https://guides.emberjs.com/v3.26.0/models/relationships/#toc_readonly-nested-data
 */

type Comparable = Record<string, any>

class CompareObject {
  compareString: string

  constructor(object: Comparable) {
    Object.assign(this, object)
    this.compareString = JSON.stringify(object)
  }

  isEqual(other?: CompareObject) {
    return this.compareString === other?.compareString
  }
}

export default class ReadonlyObjectTransform extends Transform {
  deserialize(serialized: Comparable, _options: unknown) {
    return serialized instanceof Object ? new CompareObject(serialized) : serialized
  }

  serialize(deserialized: { compareString: string }, _options: unknown): unknown {
    // Not sure on this one, disabled for the time being
    return deserialized?.compareString ? JSON.parse(deserialized.compareString) : deserialized
  }

  declare [Type]: 'readonly-object'
}
